import type {
  ILandingPageVariantItem,
  ILandingPageVariantsListResponse
} from 'services/repositories/LandingPagesRepository'

function normalizeVariantPreviewUrl(variant: Record<string, unknown>): string | null {
  const candidates = [
    variant.previewImageUrl,
    variant.preview_image_url,
    variant.imageUrl,
    variant.image_url
  ]

  for (const raw of candidates) {
    if (typeof raw === 'string' && raw.trim()) return raw.trim()
  }

  return null
}

/** Coerce API payload to the shape the UI expects (camelCase preview URLs). */
export function normalizeLandingPageVariantsListResponse(
  data: unknown
): ILandingPageVariantsListResponse {
  const root = (data ?? {}) as Record<string, unknown>
  const assetTypesRaw = root.assetTypes

  if (!Array.isArray(assetTypesRaw)) {
    return { assetTypes: [] }
  }

  return {
    assetTypes: assetTypesRaw.map(at => {
      const row = (at ?? {}) as Record<string, unknown>
      const variantsRaw = row.variants
      const variants: ILandingPageVariantItem[] = Array.isArray(variantsRaw)
        ? variantsRaw.map(v => {
            const item = (v ?? {}) as ILandingPageVariantItem &
              Record<string, unknown>

            return {
              ...item,
              name: String(item.name ?? ''),
              label:
                item.label === undefined || item.label === null
                  ? null
                  : String(item.label),
              previewImageUrl: normalizeVariantPreviewUrl(item),
              config: item.config
            }
          })
        : []

      return {
        sectionTypeId:
          row.sectionTypeId === undefined || row.sectionTypeId === null
            ? undefined
            : String(row.sectionTypeId),
        assetTypeId: String(row.assetTypeId ?? ''),
        assetTypeName: String(row.assetTypeName ?? ''),
        allowedLandingPageAssetTypeIds: Array.isArray(
          row.allowedLandingPageAssetTypeIds
        )
          ? (row.allowedLandingPageAssetTypeIds as string[])
          : undefined,
        assetTypeLimits: row.assetTypeLimits as
          | ILandingPageVariantsListResponse['assetTypes'][number]['assetTypeLimits']
          | undefined,
        variants
      }
    })
  }
}

export type VariantPreviewsMap = Record<string, Record<string, string | null>>
export type VariantLabelsMap = Record<string, Record<string, string | null>>

/** User-facing variant label; when label is empty, falls back to the internal key (`variantName`). */
export function getVariantDisplayLabel(
  variantName: string,
  label?: string | null
): string {
  const key = (variantName ?? '').trim()
  const trimmed = label?.trim()

  return trimmed && trimmed.length > 0 ? trimmed : key
}

export function buildVariantLabelMap(
  variants?: Array<{ name: string; label?: string | null }>
): Record<string, string | null> {
  const map: Record<string, string | null> = {}

  for (const v of variants ?? []) {
    map[v.name] = v.label ?? null
  }

  return map
}

/** Resolve display label from one or more maps; falls back to internal variant key. */
export function resolveVariantDisplayLabel(
  variantName: string,
  ...labelMaps: Array<Record<string, string | null> | undefined>
): string {
  for (const map of labelMaps) {
    const label = map?.[variantName]

    if (label?.trim()) return label.trim()
  }

  return getVariantDisplayLabel(variantName, null)
}

export function buildVariantDisplayMaps(
  variantsList: ILandingPageVariantsListResponse | null | undefined
): {
  variantPreviews?: VariantPreviewsMap
  variantLabels?: VariantLabelsMap
} {
  if (!variantsList?.assetTypes) return {}
  const variantPreviews: VariantPreviewsMap = {}
  const variantLabels: VariantLabelsMap = {}

  const assignForKeys = (
    keys: string[],
    name: string,
    preview: string | null,
    label: string | null
  ) => {
    for (const key of keys) {
      if (!key) continue
      if (!variantPreviews[key]) variantPreviews[key] = {}
      if (!variantLabels[key]) variantLabels[key] = {}
      variantPreviews[key][name] = preview
      variantLabels[key][name] = label
    }
  }

  for (const at of variantsList.assetTypes) {
    const keys = [
      ...(at.sectionTypeId ? [at.sectionTypeId] : []),
      at.assetTypeId
    ]

    for (const v of at.variants ?? []) {
      assignForKeys(keys, v.name, v.previewImageUrl, v.label ?? null)
    }
  }

  return { variantPreviews, variantLabels }
}

export function buildLeadVariantLabels(
  variantsList: ILandingPageVariantsListResponse | null | undefined
): Record<string, string | null> {
  const leadAt = variantsList?.assetTypes?.find(at => {
    const n = (at.assetTypeName ?? '').toLowerCase().replace(/\s+/g, '')

    return n === 'lead'
  })
  const map: Record<string, string | null> = {}

  for (const v of leadAt?.variants ?? []) {
    map[v.name] = v.label ?? null
  }

  return map
}
